import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { display_account_number } from "./coding_cell.tsx"
import { ItemBadge } from "./item_badge.tsx"
import { classes } from "@/utils"
import { BookOpenCheck, Check, ChevronsUpDown } from "lucide-react"
import { useRef, useState } from "preact/hooks"

export interface SelectItem {
  id: string
  name: string
  expense_account?: string
  account_number?: string
  /** Accounting item (product/service) this row posts through — a GL account can have one row per item. */
  product_service?: string
}

/**
 * cmdk search text for a row: the GL account number (brands know and search their chart of
 * accounts by number), the name, and the item — which also keeps sibling rows of the same GL
 * account (one per item) distinct for cmdk.
 */
export function select_item_search_value(item: SelectItem): string {
  return [item.account_number?.trim(), item.name, item.product_service].filter(Boolean).join(" ")
}

/** The account number in front of a row's name, matching the coding cell's picker. */
function AccountNumber({ item, className }: { item: SelectItem; className?: string }) {
  const number = display_account_number(item.account_number, item.expense_account || item.name)
  return number ? <span className={classes("font-mono text-xs text-gray-500 shrink-0", className)}>{number}</span> : null
}

interface AutocompleteSelectProps {
  onSelect: (match: SelectItem) => void
  values: SelectItem[]
  initial?: SelectItem
  /** Set to true when used inside a Dialog/Modal to enable proper scrolling and focus */
  insideModal?: boolean
  /** Custom trigger element (must forward its ref, e.g. CodePill) replacing the default outline button */
  trigger?: preact.ComponentChildren
  /** Search box placeholder; defaults to the reason-code wording this picker was built for. */
  searchPlaceholder?: string
  /** Text shown when nothing matches the search. */
  emptyText?: string
}

export function AutocompleteSelect({
  onSelect,
  initial,
  values,
  insideModal = false,
  trigger,
  searchPlaceholder = "Search reason code...",
  emptyText = "No codes found.",
}: AutocompleteSelectProps) {
  const [open, setOpen] = useState(false)
  const commandListRef = useRef<HTMLDivElement>(null)

  function handleSelectCode(selection: SelectItem) {
    onSelect(selection)
    // Use setTimeout to prevent the close event from bubbling up to parent dialogs
    setTimeout(() => {
      setOpen(false)
    }, 0)
  }

  function handleSearchChange() {
    // Reset scroll position when search input changes
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={insideModal}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            variant="outline"
            className={`w-full justify-start text-left font-normal ${
              initial ? "bg-white border-plue-200" : "text-plue-700"
            }`}>
            <div className="flex items-center gap-2 truncate">
              {initial ? (
                <>
                  <BookOpenCheck className="h-4 w-4 shrink-0" />
                  <AccountNumber item={initial} />
                  <span className="truncate">{initial.expense_account || initial.name}</span>
                  <ItemBadge item={initial?.product_service} className="shrink-0" />
                </>
              ) : (
                <ChevronsUpDown className="h-4 w-4 shrink-0" />
              )}
            </div>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[620px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} onValueChange={handleSearchChange} />
          <CommandList ref={commandListRef}>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {values.map((selection, index) => (
                <CommandItem
                  key={selection.id}
                  // Include the account number and item so search finds them, and so sibling rows of
                  // the same GL account (one per item) stay distinct for cmdk.
                  value={select_item_search_value(selection)}
                  className={`${index % 2 === 0 ? "bg-plue-50" : ""} ${initial?.id === selection.id ? "bg-plue-100" : ""}`}
                  onSelect={() => handleSelectCode(selection)}>
                  <Check
                    className={classes("mr-2 h-4 w-4", initial?.id === selection.id ? "opacity-100" : "opacity-0")}
                  />
                  <AccountNumber item={selection} className="mr-2" />
                  <span className="truncate">{selection.expense_account || selection.name}</span>
                  <ItemBadge item={selection.product_service} className="ml-2 shrink-0" />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("select_item_search_value", () => {
    it("lets a GL account be found by its number", () => {
      expect(select_item_search_value({ id: "a", name: "TD Checking", expense_account: "TD Checking", account_number: "1010" })).toBe(
        "1010 TD Checking"
      )
    })

    it("keeps the item so sibling rows of one account stay distinct", () => {
      expect(
        select_item_search_value({ id: "a", name: "Chargebacks", account_number: "48005", product_service: "Shortages" })
      ).toBe("48005 Chargebacks Shortages")
    })

    it("is just the name for rows without a number (customers)", () => {
      expect(select_item_search_value({ id: "c", name: "Kroger", account_number: " " })).toBe("Kroger")
    })
  })
}
